<script lang="ts" setup>
import type { ContentFile } from '~/models/Content/ContentFile'
import type { FileField } from '~/models/Content/BaseField'
import { useRouter }  from '#imports'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { faDownload } from '@fortawesome/duotone-regular-svg-icons'
import { Section } from '~/models/Content/Section'
import { ContentType } from '~/models/Content/ContentType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useFileInfo from '~/composables/useFileInfo'
import useContentApi from '~/api/contentApi'
import Button from '~/components/ui/Button.vue'

const props = defineProps<{
  file?: FileField
  contentId?: number
}>()

const router = useRouter()
const { readableMimeType } = useFileInfo()
const { buildResourceUrl } = useUrlBuilder()
const { findContents } = useContentApi()

const { data } = useQuery({
  enabled: computed(() => !!props.contentId),
  queryKey: computed(() => ['file-block', props.contentId]),
  queryFn: () => findContents<ContentFile>({
    contentIdCriterion: [props.contentId!],
    contentTypeCriterion: [ContentType.File],
  }, 1).then(([data]) => data),
  staleTime: Infinity,
})

const field = computed(() => props.file ?? data.value?.file)

const href = computed(() => {
  if (!field.value?.uri)
    return router.resolve({ name: 'notfound' }).href
  return buildResourceUrl({
    file: field.value,
    contentTypeIdentifier: ContentType.File,
    sectionId: Section.Standard,
  } as ContentFile)
})

const fileNameAndType = computed(() => {
  if (!field.value)
    return ''
  const fileType = readableMimeType(field.value.mimeType)
  const fileName = data?.value?.shortTitle || data?.value?.title || field.value?.fileName
  return !fileType ? fileName : `${fileName} (${fileType})`
})
</script>

<template>
  <Button
    :href
    class="justify-between"
    color="white"
    full-width
  >
    <span class="overflow-x-hidden text-ellipsis">{{ fileNameAndType }}</span>
    <font-awesome-icon :icon="faDownload" size="lg" />
  </Button>
</template>
